/**
 * Sender Agencies — settings card.
 *
 * Lets an admin register one or more sender agencies (e.g. "Iceberg Digital",
 * "Image Traders") with their brand identity: logo + key colours (primary,
 * secondary, two grey-scale tones) + contact details. The selected agency
 * is snapshotted onto every Proposal so its PDF / share view render the
 * right branding even if the AgencyProfile is later edited or removed.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Building2, Upload, Star, Image as ImageIcon, X,
} from "lucide-react";

const blank = () => ({
  name: "",
  logo_url: "",
  address: "",
  abn: "",
  email: "",
  phone: "",
  website: "",
  color_primary: "#002FA7",
  color_secondary: "#F03A47",
  color_grey_1: "#F4F4F4",
  color_grey_2: "#1F1F1F",
  is_default: false,
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

export default function SenderAgenciesCard() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency-profiles");
      setList(data?.agency_profiles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load agencies");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const startCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const startEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || "",
      logo_url: a.logo_url || "",
      address: a.address || "",
      abn: a.abn || "",
      email: a.email || "",
      phone: a.phone || "",
      website: a.website || "",
      color_primary: a.color_primary || "#002FA7",
      color_secondary: a.color_secondary || "#F03A47",
      color_grey_1: a.color_grey_1 || "#F4F4F4",
      color_grey_2: a.color_grey_2 || "#1F1F1F",
      is_default: !!a.is_default,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Agency name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      let saved;
      if (editing) {
        const { data } = await api.put(
          `/agency-profiles/${editing.agency_profile_id}`, payload,
        );
        saved = data;
        toast.success("Agency updated");
      } else {
        const { data } = await api.post("/agency-profiles", payload);
        saved = data;
        toast.success("Agency added");
      }
      setOpen(false); setEditing(saved); setForm({ ...form, ...saved });
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/agency-profiles/${a.agency_profile_id}`);
      toast.success("Agency removed");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const uploadLogo = async (a, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/agency-profiles/${a.agency_profile_id}/logo`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Logo uploaded");
      // Reflect immediately in the open editor if this agency is being edited.
      if (editing?.agency_profile_id === a.agency_profile_id) {
        setForm((f) => ({ ...f, logo_url: data.logo_url }));
        setEditing({ ...editing, logo_url: data.logo_url });
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const clearLogo = async (a) => {
    try {
      await api.delete(`/agency-profiles/${a.agency_profile_id}/logo`);
      if (editing?.agency_profile_id === a.agency_profile_id) {
        setForm((f) => ({ ...f, logo_url: "" }));
        setEditing({ ...editing, logo_url: "" });
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="sender-agencies-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">Sender agencies</div>
              <div className="text-xs text-zinc-500 max-w-lg">
                Register one or more agencies (e.g. Iceberg Digital, Image
                Traders) with their logo + brand colours. The selected
                agency is snapshotted onto every proposal&apos;s PDF and
                share view.
              </div>
            </div>
          </div>
          <Button onClick={startCreate}
            className="rounded-sm bg-zinc-900 hover:bg-black text-white h-9"
            data-testid="sender-agency-add-button">
            <Plus className="h-4 w-4 mr-1" /> Add agency
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : list.length === 0 ? (
          <div className="rounded-sm border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500"
            data-testid="sender-agency-empty">
            No sender agencies yet — click <strong>Add agency</strong> to
            register one (e.g. <em>Image Traders</em>).
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((a) => (
              <AgencyRow key={a.agency_profile_id} a={a}
                onEdit={() => startEdit(a)}
                onRemove={() => remove(a)}
                onUploadLogo={(f) => uploadLogo(a, f)}
                onClearLogo={() => clearLogo(a)} />
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl rounded-sm"
          data-testid="sender-agency-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-2xl tracking-tight">
              {editing ? "Edit sender agency" : "New sender agency"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Set the agency&apos;s brand identity — logo, key colours and
              contact details. Used on proposals and the public share view.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Agency name" required value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testid="sa-name" placeholder="Image Traders" />
              <Field label="Website" value={form.website}
                onChange={(v) => setForm({ ...form, website: v })}
                testid="sa-website" placeholder="https://imagetraders.com.au" />
              <Field label="Email" type="email" value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testid="sa-email" placeholder="hello@imagetraders.com.au" />
              <Field label="Phone" value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                testid="sa-phone" placeholder="+61 ..." />
              <Field label="ABN" value={form.abn}
                onChange={(v) => setForm({ ...form, abn: v })}
                testid="sa-abn" />
              <Field label="Address" value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                testid="sa-address" />
            </div>

            {/* Logo */}
            <div className="space-y-2 pt-3 border-t border-zinc-200">
              <Label className="label-overline">Agency logo</Label>
              <div className="flex items-center gap-4">
                <LogoPreview url={form.logo_url} />
                {editing ? (
                  <div className="flex items-center gap-2">
                    <LogoUploadButton
                      onPick={(f) => uploadLogo(editing, f)}
                      testid="sa-logo-upload" />
                    {form.logo_url && (
                      <Button type="button" variant="ghost" size="sm"
                        className="rounded-sm text-red-600"
                        onClick={() => clearLogo(editing)}
                        data-testid="sa-logo-clear">
                        <X className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-[11px] text-zinc-500 italic">
                    Save the agency first, then upload the logo from the
                    row below or by reopening this editor.
                  </p>
                )}
              </div>
            </div>

            {/* Brand colours */}
            <div className="space-y-2 pt-3 border-t border-zinc-200">
              <Label className="label-overline">Brand colours</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ColorField label="Primary" value={form.color_primary}
                  onChange={(v) => setForm({ ...form, color_primary: v })}
                  testid="sa-color-primary" />
                <ColorField label="Secondary" value={form.color_secondary}
                  onChange={(v) => setForm({ ...form, color_secondary: v })}
                  testid="sa-color-secondary" />
                <ColorField label="Grey 1 (light)" value={form.color_grey_1}
                  onChange={(v) => setForm({ ...form, color_grey_1: v })}
                  testid="sa-color-grey-1" />
                <ColorField label="Grey 2 (dark)" value={form.color_grey_2}
                  onChange={(v) => setForm({ ...form, color_grey_2: v })}
                  testid="sa-color-grey-2" />
              </div>
            </div>

            {/* Default toggle */}
            <label className="flex items-center gap-2 text-xs text-zinc-700 pt-3 border-t border-zinc-200"
              data-testid="sa-default-row">
              <input type="checkbox" checked={!!form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                data-testid="sa-default-toggle" />
              Use this agency as the default sender on new proposals.
            </label>

            <DialogFooter>
              <Button type="button" variant="ghost" className="rounded-sm"
                onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}
                className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="sa-submit">
                {busy ? "Saving…" : (editing ? "Save changes" : "Add agency")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AgencyRow({ a, onEdit, onRemove, onUploadLogo, onClearLogo }) {
  return (
    <div className="grid grid-cols-[64px_1fr_auto] gap-4 items-center border border-zinc-200 rounded-sm p-3"
      data-testid={`sender-agency-row-${a.agency_profile_id}`}>
      <LogoPreview url={a.logo_url} size={56} />
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-sm truncate">{a.name}</span>
          {a.is_default && (
            <Badge className="rounded-sm bg-amber-100 text-amber-700 text-[9px] font-mono px-1.5">
              <Star className="h-2.5 w-2.5 mr-1 fill-amber-700" /> default
            </Badge>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
          {[a.email, a.phone, a.website].filter(Boolean).join(" · ") || "—"}
        </div>
        <div className="flex items-center gap-1 mt-1.5">
          <Swatch color={a.color_primary} label="Primary" />
          <Swatch color={a.color_secondary} label="Secondary" />
          <Swatch color={a.color_grey_1} label="Grey 1" />
          <Swatch color={a.color_grey_2} label="Grey 2" />
        </div>
      </div>
      <div className="flex items-center gap-1">
        <LogoUploadButton
          onPick={onUploadLogo}
          testid={`sender-agency-${a.agency_profile_id}-logo`} compact />
        {a.logo_url && (
          <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-red-600"
            onClick={onClearLogo}
            title="Remove logo"
            data-testid={`sender-agency-${a.agency_profile_id}-logo-clear`}>
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
          onClick={onEdit}
          data-testid={`sender-agency-${a.agency_profile_id}-edit`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-red-600"
              data-testid={`sender-agency-${a.agency_profile_id}-delete`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {a.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                Existing proposals keep their snapshotted branding — only
                future proposals will lose the option to pick this agency.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                onClick={onRemove}
                data-testid={`sender-agency-${a.agency_profile_id}-confirm-delete`}>
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function LogoPreview({ url, size = 48 }) {
  if (!url) {
    return (
      <div className="rounded-sm bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400"
        style={{ width: size, height: size }}>
        <ImageIcon className="h-5 w-5" />
      </div>
    );
  }
  return (
    <img src={absUrl(url)} alt="" className="rounded-sm object-contain bg-white border border-zinc-200"
      style={{ width: size, height: size }} />
  );
}

function LogoUploadButton({ onPick, testid, compact = false }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
        data-testid={`${testid}-input`} />
      <Button type="button" variant={compact ? "ghost" : "outline"} size="sm"
        className={compact ? "rounded-sm h-8 w-8 p-0" : "rounded-sm"}
        onClick={() => ref.current?.click()}
        title="Upload logo"
        data-testid={`${testid}-button`}>
        <Upload className="h-3.5 w-3.5" />
        {!compact && <span className="ml-1.5 text-xs">Upload logo</span>}
      </Button>
    </>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-overline">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded-sm border border-zinc-200 cursor-pointer p-0"
          data-testid={`${testid}-picker`} />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="rounded-sm font-mono text-xs h-9"
          placeholder="#000000"
          data-testid={`${testid}-input`} />
      </div>
    </div>
  );
}

function Swatch({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500"
      title={`${label} — ${color}`}>
      <span className="h-3 w-3 rounded-sm border border-zinc-300"
        style={{ background: color || "#fff" }} />
    </span>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-overline">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </Label>
      <Input type={type} required={required} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm" data-testid={`${testid}-input`} />
    </div>
  );
}
